<script lang="ts" setup>
import { computed, ref, type PropType, watch, type ComputedRef } from 'vue';
import { getContainer, getContext } from '@dandelion/core';
import { CaseListServiceInterface } from '@dandelion/core';
import { NamedBindings } from '@dandelion/core';
import { ApplicationEvents } from '@dandelion/core';

const props = defineProps({
  context: {
    type: String as PropType<string>,
    required: true,
  },
  numberOfElementsPerPage: {
    type: Number as PropType<number>,
    required: true,
  },
  maxPagesDefaultView: {
    type: Number as PropType<number>,
    default: 8,
    validator: (value: number) => value >= 8,
  },
  deletedCaseList: {
    //TODO: try to remove
    type: Boolean as PropType<boolean>,
    required: true,
  },
});

const container = getContainer(props.context);
const caseListService = container.get(NamedBindings.CaseListService) as CaseListServiceInterface;

const numberOfCases = computed(() => {
  if (caseListService.areFiltersActive.value) {
    return props.deletedCaseList ? caseListService.filteredDeletedCases.length : caseListService.filteredCases.length;
  }
  return props.deletedCaseList ? caseListService.allDeletedCases.length : caseListService.allCases.length;
});

const currentPage = ref(1);
const morePages = '...';
const showPreviousPagesList = ref(false);
const showNextPagesList = ref(false);
const totalPagesCount = computed(() => Math.max(Math.ceil(numberOfCases.value / props.numberOfElementsPerPage), 1));

const emit = defineEmits<{
  (e: 'update', value: { startIndex: number; endIndex: number }): void;
}>();

getContext(props.context).addEventListener(ApplicationEvents.CaseListUpdated, () => {
  if (currentPage.value > totalPagesCount.value) {
    currentPage.value = totalPagesCount.value;
  }
});

watch(currentPage, (newPage) => {
  const startIndex = (newPage - 1) * props.numberOfElementsPerPage;
  const endIndex = newPage * props.numberOfElementsPerPage;
  emit('update', {
    startIndex,
    endIndex,
  });
});

const displayedPages = computed<number[]>(() => {
  if (currentPage.value === 1 || currentPage.value === 2) {
    return [2, 3, 4];
  }
  if (currentPage.value === totalPagesCount.value || currentPage.value === totalPagesCount.value - 1) {
    return [totalPagesCount.value - 2, totalPagesCount.value - 1];
  }
  return [currentPage.value - 1, currentPage.value, currentPage.value + 1];
});

const previousPages = computed<number[]>(() => Array.from({ length: currentPage.value - 3 }, (_, i) => i + 2));

const nextPages = computed<number[]>(() => {
  let length = 0;
  let start = 0;
  if (currentPage.value === 1) {
    length = totalPagesCount.value - currentPage.value - 4;
    start = currentPage.value + 4;
  } else if (currentPage.value === 2) {
    length = totalPagesCount.value - currentPage.value - 3;
    start = currentPage.value + 3;
  } else {
    length = totalPagesCount.value - currentPage.value - 2;
    start = currentPage.value + 2;
  }
  return Array.from({ length }, (_, i) => start + i);
});

function previousPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
}

function nextPage() {
  if (currentPage.value < totalPagesCount.value) {
    currentPage.value++;
  }
}

function setCurrentPage(page: number) {
  currentPage.value = page;
  showPreviousPagesList.value = false;
  showNextPagesList.value = false;
}

function openPreviousPagesList() {
  showPreviousPagesList.value = !showPreviousPagesList.value;
  showNextPagesList.value = false;
}

function openNextPagesList() {
  showNextPagesList.value = !showNextPagesList.value;
  showPreviousPagesList.value = false;
}
</script>

<template>
  <section class="case-list-pagination" v-if="numberOfCases > numberOfElementsPerPage">
    <button
      type="button"
      @click.stop="previousPage"
      class="previous-page-btn case-list-pagination-btn"
      :disabled="currentPage === 1"
    >
      <ion-icon name="chevron-back" />
    </button>
    <template v-if="totalPagesCount < maxPagesDefaultView">
      <button
        type="button"
        v-for="page in totalPagesCount"
        :key="page"
        @click.stop="setCurrentPage(page)"
        :class="['case-list-pagination-btn', { 'active-page': currentPage === page }]"
        v-html="page"
      />
    </template>
    <template v-else>
      <button
        type="button"
        @click.stop="setCurrentPage(1)"
        :class="['case-list-pagination-btn', { 'active-page': currentPage === 1 }]"
        v-html="'1'"
      />

      <button
        type="button"
        v-if="currentPage > 3"
        @click.stop="openPreviousPagesList"
        class="case-list-pagination-btn"
        v-html="morePages"
      />

      <button
        type="button"
        v-for="page in displayedPages"
        :key="page"
        @click.stop="setCurrentPage(page)"
        :class="['case-list-pagination-btn', { 'active-page': currentPage === page }]"
        v-html="page"
      />

      <button
        type="button"
        v-if="currentPage < totalPagesCount - 2"
        @click.stop="openNextPagesList"
        class="case-list-pagination-btn"
        v-html="morePages"
      />
      <button
        type="button"
        @click.stop="setCurrentPage(totalPagesCount)"
        :class="['case-list-pagination-btn', { 'active-page': currentPage === totalPagesCount }]"
        v-html="totalPagesCount"
      />

      <div v-if="showPreviousPagesList" class="previous-pages-list-wrapper pages-list-wrapper">
        <div class="previous-pages-list">
          <span v-for="page in previousPages" :key="`prev-${page}`" @click.stop="setCurrentPage(page)" v-html="page" />
        </div>
      </div>

      <div v-if="showNextPagesList" class="next-pages-list-wrapper pages-list-wrapper">
        <div class="next-pages-list">
          <span v-for="page in nextPages" :key="`next-${page}`" @click.stop="setCurrentPage(page)" v-html="page" />
        </div>
      </div>
    </template>
    <button
      type="button"
      @click.stop="nextPage"
      :disabled="currentPage === totalPagesCount"
      class="next-page-btn case-list-pagination-btn"
    >
      <ion-icon name="chevron-forward" />
    </button>
  </section>
</template>

<style>
.pagination {
  display: flex;
  align-items: center;
  position: relative;
  max-width: fit-content;
  margin: 1rem 0;
}

.active-page {
  background-color: #4caf50;
  color: white;
}

.pages-list-wrapper {
  position: absolute;
  top: 4rem;
  width: 7.5rem;
  height: fit-content;
  background: #d7dde4;
}

.pages-list-wrapper::after {
  content: '';
  position: absolute;
  top: -1.5rem;
  right: calc(50% - 10px);
  border-width: 0.75rem;
  border-style: solid;
  border-color: transparent transparent #d7dde4 transparent;
}

#previous-pages-list-wrapper {
  left: 240px;
}

#next-pages-list-wrapper {
  right: 240px;
}

#previous-pages-list,
#next-pages-list {
  display: flex;
  flex-direction: column;
  overflow: auto;
  max-height: 10rem;
  padding: 0.5rem 0;
}

#previous-pages-list span,
#next-pages-list span {
  padding-left: 0.5rem;
}

#previous-pages-list span:hover,
#next-pages-list span:hover {
  background-color: grey;
}
</style>
